<template>
  <div class="modal-overlay top help-backdrop" @click.self="$emit('close')">
    <div class="help-modal" role="dialog" aria-modal="true" aria-label="FFTBG help">
      <button class="close-btn" @click="$emit('close')" aria-label="Close">×</button>

      <h1>FFTBG Guide</h1>

      <a class="help-faq-banner" href="?faq">❓ Got a question? The community <strong>FAQ</strong> answers what chat asks most — auto-mined from six years of stream chat →</a>

      <!-- Mobile section picker (sidebar is hidden under 700px) -->
      <select class="section-select" v-model="activeId">
        <option v-for="s in sections" :key="s.id" :value="s.id">{{ s.title }}</option>
      </select>

      <div class="help-body">
        <nav class="help-nav">
          <button v-for="s in sections" :key="s.id"
                  :class="['nav-item', { active: s.id === activeId }]"
                  @click="activeId = s.id">{{ s.title }}</button>
        </nav>

        <div class="help-content" ref="content">
          <h2>{{ activeSection.title }}</h2>

          <template v-for="(block, i) in activeSection.blocks" :key="i">
            <p v-if="block.type === 'p'" class="b-p">{{ block.text }}</p>

            <h3 v-else-if="block.type === 'h'" class="b-h">{{ block.text }}</h3>

            <div v-else-if="block.type === 'callout'" class="b-callout">{{ block.text }}</div>

            <ul v-else-if="block.type === 'list'" class="b-list">
              <li v-for="(item, j) in block.items" :key="j">{{ item }}</li>
            </ul>

            <div v-else-if="block.type === 'cmd'" class="b-cmd">
              <code>{{ block.name }}</code>
              <span v-if="block.desc" class="b-cmd-desc">{{ block.desc }}</span>
            </div>

            <a v-else-if="block.type === 'link'" class="b-link"
               :href="block.url" target="_blank" rel="noopener">{{ block.label }}</a>

            <ul v-else-if="block.type === 'resources'" class="b-resources">
              <li v-for="(r, j) in block.items" :key="j">
                <a v-if="r.url" class="b-link" :href="r.url" target="_blank" rel="noopener">{{ r.label }}</a>
                <strong v-else>{{ r.label }}</strong>
                <span v-if="r.by" class="res-by"> — {{ r.by }}</span>
                <template v-if="r.desc">: {{ r.desc }}</template>
              </li>
            </ul>

            <div v-else-if="block.type === 'commands'" class="b-commands">
              <input type="text" class="cmd-search" v-model="cmdQuery"
                     placeholder="Search commands…">
              <div class="cmd-chips">
                <button :class="['chip', { active: cmdCategory === 'all' }]"
                        @click="cmdCategory = 'all'">All</button>
                <button v-for="cat in block.categories" :key="cat.name"
                        :class="['chip', { active: cmdCategory === cat.name }]"
                        @click="cmdCategory = cat.name">{{ cat.name }}</button>
              </div>

              <p v-if="!filteredCommands(block).length" class="cmd-empty">No commands match.</p>
              <table v-else class="cmd-table">
                <tbody>
                  <tr v-for="(c, k) in filteredCommands(block)" :key="k">
                    <td class="cmd-name"><code>{{ c.name }}</code> <span class="cmd-params">{{ c.params }}</span></td>
                    <td class="cmd-desc">{{ c.desc }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>

      <p class="help-footer">
        Source:
        <a :href="sourceUrl" target="_blank" rel="noopener">FFTBG Twitch description</a>,
        captured {{ capturedDate }}.
      </p>
    </div>
  </div>
</template>

<script>
import { HELP_SECTIONS, HELP_CAPTURED_DATE, HELP_SOURCE_URL } from '../data/helpContent.js';
import modalA11y from '../mixins/modalA11y.js';

export default {
  mixins: [modalA11y],
  emits: ['close'],

  data() {
    return {
      sections: HELP_SECTIONS,
      capturedDate: HELP_CAPTURED_DATE,
      sourceUrl: HELP_SOURCE_URL,
      activeId: HELP_SECTIONS[0].id,
      // Command-table state (only relevant on the Commands section).
      cmdQuery: '',
      cmdCategory: 'all',
    };
  },

  computed: {
    activeSection() {
      return this.sections.find(s => s.id === this.activeId) || this.sections[0];
    },
  },

  watch: {
    // Scroll the content pane back to the top when switching sections, and
    // reset the command filters so they don't leak between visits.
    activeId() {
      this.cmdQuery = '';
      this.cmdCategory = 'all';
      this.$nextTick(() => {
        if (this.$refs.content) this.$refs.content.scrollTop = 0;
      });
    },
  },

  methods: {
    // Flatten the chosen category's commands (or all), then filter by the
    // search box against command name + description.
    filteredCommands(block) {
      const cats = this.cmdCategory === 'all'
        ? block.categories
        : block.categories.filter(c => c.name === this.cmdCategory);
      const all = cats.flatMap(c => c.cmds);
      const q = this.cmdQuery.trim().toLowerCase();
      if (!q) return all;
      return all.filter(c =>
        (c.name + ' ' + (c.params || '') + ' ' + c.desc).toLowerCase().includes(q)
      );
    },
  },
};
</script>

<style scoped>
.help-backdrop {
  background: rgba(0, 0, 0, 0.6);
  padding: 40px 16px;
}
.help-modal {
  position: relative;
  width: 100%;
  max-width: 860px;
  background: rgba(12, 54, 80, 0.97);
  border: 2px solid #fff;
  color: #fff;
  padding: 24px 28px 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 14px;
}
.close-btn {
  position: absolute;
  top: 6px;
  right: 10px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
  font-family: inherit;
}
.close-btn:hover { color: #ff5555; }

.help-modal h1 {
  font-family: "BossBattle";
  font-size: 28px;
  text-align: center;
  margin: 0 0 14px;
  -webkit-text-stroke: .1px #000;
}

.help-faq-banner {
  display: block;
  margin: 0 0 14px;
  padding: 9px 14px;
  background: rgba(28, 110, 164, 0.25);
  border: 1px solid #4a9fd8;
  border-radius: 6px;
  color: #d6ecff;
  text-decoration: none;
  font-size: 13px;
  line-height: 1.4;
}
.help-faq-banner:hover { background: rgba(28, 110, 164, 0.45); color: #fff; }
.help-faq-banner strong { color: #ffe6a8; }

.section-select {
  display: none;
  width: 100%;
  margin-bottom: 12px;
  padding: 6px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  border: 1px solid #fff;
  font-family: inherit;
  font-size: 14px;
}

.help-body {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.help-nav {
  flex: 0 0 180px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  padding-right: 12px;
}
.nav-item {
  text-align: left;
  background: transparent;
  border: none;
  color: #fff;
  padding: 6px 10px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  opacity: 0.75;
}
.nav-item:hover { background: rgba(255, 255, 255, 0.08); opacity: 1; }
.nav-item.active {
  background: #1C6EA4;
  opacity: 1;
  font-weight: bold;
}

.help-content {
  flex: 1 1 auto;
  min-width: 0;
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 4px;
}
.help-content h2 {
  font-family: "BossBattle";
  font-size: 18px;
  margin: 0 0 12px;
  -webkit-text-stroke: .1px #000;
}

.b-p { line-height: 1.5; margin: 0 0 12px; }
.b-h {
  font-family: "BossBattle";
  font-size: 14px;
  margin: 18px 0 8px;
}
.b-callout {
  border-left: 3px solid #ffd700;
  background: rgba(255, 215, 0, 0.1);
  padding: 8px 12px;
  margin: 0 0 12px;
  line-height: 1.45;
  font-size: 13px;
}
.b-list { margin: 0 0 12px; padding-left: 20px; line-height: 1.5; }
.b-list li { margin: 3px 0; font-size: inherit; list-style: disc; }

.b-cmd { margin: 0 0 8px; line-height: 1.45; }
.b-cmd-desc { margin-left: 8px; opacity: 0.9; }

.b-link {
  display: inline-block;
  color: #00ff00;
  margin: 0 12px 8px 0;
}

.b-resources { margin: 0 0 12px; padding-left: 20px; line-height: 1.55; }
.b-resources li { margin: 6px 0; }
.b-resources .b-link { display: inline; margin: 0; }
.res-by { opacity: 0.75; }

code {
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 1px 6px;
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 12px;
  white-space: nowrap;
}

.cmd-search {
  width: 100%;
  box-sizing: border-box;
  margin: 8px 0;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid #fff;
  color: #fff;
  font-family: inherit;
  font-size: 13px;
}
.cmd-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.chip {
  background: transparent;
  border: 1px solid #fff;
  color: #fff;
  padding: 3px 10px;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}
.chip:hover { background: rgba(255, 255, 255, 0.1); }
.chip.active { background: #1C6EA4; }

.cmd-empty { opacity: 0.7; font-style: italic; }
.cmd-table { width: 100%; border-collapse: collapse; }
.cmd-table td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding: 6px 8px 6px 0;
  vertical-align: top;
  line-height: 1.4;
}
.cmd-name { white-space: nowrap; }
.cmd-params { font-size: 12px; opacity: 0.7; margin-left: 4px; }
.cmd-desc { font-size: 13px; }

.help-footer {
  margin: 16px 0 0;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 11px;
  opacity: 0.65;
  text-align: center;
}
.help-footer a { color: #00ff00; }

@media (max-width: 700px) {
  .help-nav { display: none; }
  .section-select { display: block; }
  .help-content { max-height: none; }
}
</style>
